import React, { useState, useCallback, useRef, useEffect } from 'react';
import { View, Pressable, StyleSheet, Platform, PanResponder } from 'react-native';
import { useTheme } from '@/context/ThemeContext';

const DOUBLE_CLICK_MS = 300;

export default function DeckColumnResizeHandle({ onResize, onDoubleClick }) {
  const { colors } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const lastClickTime = useRef(0);
  const lastReportedX = useRef(0);

  // Track teardown for any in-progress web drag so we can guarantee cleanup
  // if the component unmounts while the user is mid-resize. Without this,
  // the document-level mousemove/mouseup listeners outlive the component
  // and continue to call `onResize` on a stale closure (and leave the body
  // cursor stuck on `col-resize`).
  const cleanupRef = useRef(null);

  const onResizeRef = useRef(onResize);
  const onDoubleClickRef = useRef(onDoubleClick);
  useEffect(() => {
    onResizeRef.current = onResize;
    onDoubleClickRef.current = onDoubleClick;
  }, [onResize, onDoubleClick]);

  const panResponder = useRef(
    Platform.OS === 'macos'
      ? PanResponder.create({
          onStartShouldSetPanResponder: () => true,
          onMoveShouldSetPanResponder: () => true,
          onPanResponderGrant: (e) => {
            const now = Date.now();
            if (now - lastClickTime.current < DOUBLE_CLICK_MS) {
              lastClickTime.current = 0;
              onDoubleClickRef.current?.();
              return;
            }
            lastClickTime.current = now;
            setIsDragging(true);
            lastReportedX.current = e.nativeEvent.pageX;
          },
          onPanResponderMove: (e) => {
            const currentX = e.nativeEvent.pageX;
            const delta = currentX - lastReportedX.current;
            lastReportedX.current = currentX;
            onResizeRef.current?.(delta, true);
          },
          onPanResponderRelease: () => setIsDragging(false),
          onPanResponderTerminate: () => setIsDragging(false),
        })
      : null
  ).current;

  const handleMouseDown = useCallback((e) => {
    if (Platform.OS !== 'web') return;
    e.preventDefault();

    const now = Date.now();
    if (now - lastClickTime.current < DOUBLE_CLICK_MS) {
      lastClickTime.current = 0;
      onDoubleClickRef.current?.();
      return;
    }
    lastClickTime.current = now;

    setIsDragging(true);
    lastReportedX.current = e.clientX;
    let rafId = 0;
    let pendingX = null;
    const flushResize = () => {
      if (pendingX == null) return;
      const delta = pendingX - lastReportedX.current;
      lastReportedX.current = pendingX;
      pendingX = null;
      if (delta) onResizeRef.current?.(delta, true);
    };

    const handleMouseMove = (moveEvent) => {
      pendingX = moveEvent.clientX;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        flushResize();
      });
    };

    const cleanup = () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      cleanupRef.current = null;
    };

    const handleMouseUp = () => {
      flushResize();
      setIsDragging(false);
      cleanup();
    };

    cleanupRef.current = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Belt-and-suspenders: tear down any pending listeners on unmount.
  useEffect(() => () => {
    cleanupRef.current?.();
  }, []);

  return (
    <Pressable
      onMouseDown={Platform.OS === 'web' ? handleMouseDown : undefined}
      {...(Platform.OS === 'macos' && panResponder ? panResponder.panHandlers : {})}
      style={({ hovered }) => [
        styles.handle,
        {
          backgroundColor: (hovered || isDragging) ? colors.border_color_primary : 'transparent',
        },
      ]}
      accessibilityRole="adjustable"
      accessibilityLabel="Resize column"
      accessibilityActions={[{ name: 'increment' }, { name: 'decrement' }]}
      onAccessibilityAction={(e) => {
        if (e.nativeEvent.actionName === 'increment') onResizeRef.current?.(20, true);
        if (e.nativeEvent.actionName === 'decrement') onResizeRef.current?.(-20, true);
      }}
      {...(Platform.OS === 'web' ? {
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          onResizeRef.current?.(e.key === 'ArrowRight' ? 20 : -20, true);
        },
      } : {})}
    >
      <View
        pointerEvents="none"
        style={[
          styles.line,
          { backgroundColor: isDragging ? colors.border_color_primary : colors.border_color_secondary },
        ]}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  handle: {
    width: 6,
    cursor: 'col-resize',
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: 5,
    flexShrink: 0,
  },
  line: {
    width: 1,
    height: '100%',
  },
});
